import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { Mic2 } from 'lucide-react';
import { useNostrLogin } from '@nostrify/react/login';
import { Navbar } from '@/components/Navbar';
import { EpisodeCard, EpisodeCardSkeleton } from '@/components/EpisodeCard';
import { useEpisodes } from '@/hooks/useEpisodes';
import { Card, CardContent } from '@/components/ui/card';
import { SupportSection } from '@/components/SupportSection';

function EpisodesFeed() {
  const { data: episodes, isLoading, isError, error } = useEpisodes();

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {Array.from({ length: 6 }).map((_, i) => (
          <EpisodeCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-16 px-8 text-center">
          <Mic2 className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-40" />
          <p className="text-muted-foreground max-w-sm mx-auto">
            Could not load episodes. Check your relay connections and try again.
          </p>
          <p className="text-xs text-red-400 mt-2 font-mono">{String(error)}</p>
        </CardContent>
      </Card>
    );
  }

  if (!episodes || episodes.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-16 px-8 text-center">
          <Mic2 className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-40" />
          <p className="text-muted-foreground max-w-sm mx-auto">
            No episodes found. Make sure your relay is connected.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {episodes.map((episode) => (
        <EpisodeCard key={episode.event.id} episode={episode} />
      ))}
    </div>
  );
}

const Index = () => {
  const { logins } = useNostrLogin();
  const navigate = useNavigate();

  useEffect(() => {
    if (logins.length === 0) {
      navigate('/', { replace: true });
    }
  }, [logins, navigate]);

  useSeoMeta({
    title: 'Simply Nostr — Episodes',
    description: 'Conversations with the people of Nostr.',
  });

  if (logins.length === 0) return null;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10 pb-20">
        <div className="mb-8">
          <h2 className="text-2xl font-bold tracking-tight">All Episodes</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Every conversation, live from Nostr
          </p>
        </div>

        <EpisodesFeed />
      </main>

      <SupportSection />

      <footer className="border-t border-border/30 py-6 text-center text-xs text-muted-foreground/50">
        Vibed with{' '}
        <a
          href="https://shakespeare.diy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-purple-400/70 hover:text-purple-400 transition-colors"
        >
          Shakespeare
        </a>
      </footer>
    </div>
  );
};

export default Index;
